/**
 * `AddClientDialog` — the two-tabbed modal for adding a client
 * (ADR-0237 §6).
 *
 *   Tab A — Create account: creates a `pending_owner` client account
 *   plus an active `agency_created` link. The agency NEVER sees the
 *   owner's magic-link token; the server emails the owner.
 *
 *   Tab B — Link existing account: emails the client owner a
 *   per-request approval token. The agency sees "Request sent" copy
 *   only; access is granted when the owner approves.
 */

import { useState } from 'react';

import type { AgencyGrantedRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  createClient,
  createLinkRequest,
} from '../../api/agency.js';
import { useC } from '../../lib/theme.js';
import { Field, Modal } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface AddClientDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create/link-request so the parent can
   *  refresh its list. The dialog closes itself. */
  onCreated: () => void;
}

export function AddClientDialog({
  open,
  onClose,
  onCreated,
}: AddClientDialogProps): JSX.Element | null {
  const C = useC();
  const [tab, setTab] = useState<'create' | 'link'>('create');

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Add client"
      subtitle="Create a new account for the client, or ask an existing account owner for access."
      closeOnBackdrop={false}
    >
      <div
        className="px-6 pt-2 pb-6"
        style={{ fontFamily: sansFont }}
      >
        <div
          role="tablist"
          className="flex items-center gap-1 mb-5"
          style={{
            borderBottom: `1px solid ${C.borderSoft}`,
          }}
        >
          <TabBtn active={tab === 'create'} onClick={() => setTab('create')} id="t-create">
            Create account
          </TabBtn>
          <TabBtn active={tab === 'link'} onClick={() => setTab('link')} id="t-link">
            Link existing account
          </TabBtn>
        </div>
        {tab === 'create' ? (
          <CreateForm onDone={onCreated} onCancel={onClose} />
        ) : (
          <LinkForm onDone={onCreated} onCancel={onClose} />
        )}
      </div>
    </Modal>
  );
}

function TabBtn({
  active,
  onClick,
  id,
  children,
}: {
  active: boolean;
  onClick: () => void;
  id: string;
  children: React.ReactNode;
}): JSX.Element {
  const C = useC();
  return (
    <button
      role="tab"
      aria-selected={active}
      id={id}
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        borderBottom: `2px solid ${active ? C.text : 'transparent'}`,
        color: active ? C.text : C.muted,
        fontFamily: sansFont,
        fontSize: 13,
        fontWeight: 500,
        padding: '8px 10px',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

interface SubFormProps {
  onDone: () => void;
  onCancel: () => void;
}

function CreateForm({ onDone, onCancel }: SubFormProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'owner' | 'admin'>('owner');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (): Promise<void> => {
    if (http === null || submitting) return;
    setError(null);
    setSuccess(null);
    const trimmed_name = name.trim();
    const trimmed_domain = domain.trim().toLowerCase();
    const trimmed_email = email.trim().toLowerCase();
    if (trimmed_name.length === 0) {
      setError('Enter the client’s account name.');
      return;
    }
    if (trimmed_domain.length === 0) {
      setError('Enter the client’s primary domain (e.g. acme.com).');
      return;
    }
    if (trimmed_email.length === 0) {
      setError('Enter the owner’s email.');
      return;
    }
    setSubmitting(true);
    const r = await createClient(http, {
      name: trimmed_name,
      primary_domain: trimmed_domain,
      owner_email: trimmed_email,
      invite_role: role,
    });
    setSubmitting(false);
    if (r.kind === 'ok') {
      setSuccess('Client account created. The owner has 7 days to accept the invite.');
      // Close shortly after so the user sees the confirmation; parent
      // refreshes.
      setTimeout(() => {
        onDone();
      }, 1200);
      return;
    }
    if (r.kind === 'owner_email_taken') {
      setError('That email already has a Sill account. Use Link existing account instead.');
      return;
    }
    if (r.kind === 'invalid') {
      setError(humanizeInvalid(r.reason));
      return;
    }
    if (r.kind === 'forbidden') {
      setError(humanizeForbidden(r.reason));
      return;
    }
    if (r.kind === 'rate_limited') {
      setError(r.message);
      return;
    }
    setError(r.message);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
      className="space-y-4"
    >
      <Field label="Account name" required>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Inc"
          autoFocus
          style={inputStyle(C)}
        />
      </Field>
      <Field label="Primary domain" required hint="Lowercase, no scheme. Example: acme.com">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="acme.com"
          style={inputStyle(C)}
        />
      </Field>
      <Field label="Owner email" required hint="The owner clicks a 7-day invite link. You never see their token.">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="founder@acme.com"
          style={inputStyle(C)}
        />
      </Field>
      <Field label="Invite as">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value === 'admin' ? 'admin' : 'owner')}
          style={inputStyle(C)}
        >
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
        </select>
      </Field>
      {error !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
      ) : null}
      {success !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg }}>{success}</div>
      ) : null}
      <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="w-full sm:w-auto px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || success !== null}
          className="w-full sm:flex-1 px-4 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 2,
            opacity: submitting || success !== null ? 0.6 : 1,
            cursor: submitting || success !== null ? 'default' : 'pointer',
          }}
        >
          {submitting
            ? 'Creating…'
            : success !== null
              ? 'Created'
              : 'Create account'}
        </button>
      </div>
    </form>
  );
}

function LinkForm({ onDone, onCancel }: SubFormProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [email, setEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [role, setRole] = useState<AgencyGrantedRole>('admin');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (): Promise<void> => {
    if (http === null || submitting) return;
    setError(null);
    setSuccess(null);
    const trimmed_email = email.trim().toLowerCase();
    const trimmed_domain = domain.trim().toLowerCase();
    if (trimmed_email.length === 0 && trimmed_domain.length === 0) {
      setError('Enter the client owner’s email or their domain.');
      return;
    }
    setSubmitting(true);
    const body: Parameters<typeof createLinkRequest>[1] = {
      requested_role: role,
    };
    if (trimmed_email.length > 0) {
      (body as { client_email: string }).client_email = trimmed_email;
    }
    if (trimmed_domain.length > 0) {
      (body as { client_domain: string }).client_domain = trimmed_domain;
    }
    const r = await createLinkRequest(http, body);
    setSubmitting(false);
    if (r.kind === 'ok') {
      const d = formatExpiry(r.response.expires_at);
      setSuccess(`Request sent. Access starts when the account owner approves. The request expires on ${d}.`);
      setTimeout(() => {
        onDone();
      }, 1800);
      return;
    }
    if (r.kind === 'invalid') {
      setError(humanizeInvalid(r.reason));
      return;
    }
    if (r.kind === 'forbidden') {
      setError(humanizeForbidden(r.reason));
      return;
    }
    if (r.kind === 'rate_limited') {
      setError(r.message);
      return;
    }
    setError(r.message);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
      className="space-y-4"
    >
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
        We email the owner an approval link. Access starts only after they
        approve on their own account.
      </div>
      <Field label="Client owner email" hint="Preferred — resolves to an exact account.">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="founder@acme.com"
          style={inputStyle(C)}
        />
      </Field>
      <Field label="Or client domain" hint="Used when you don’t have the owner’s email.">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="acme.com"
          style={inputStyle(C)}
        />
      </Field>
      <Field label="Requested role">
        <select
          value={role}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'admin' || v === 'reviewer' || v === 'viewer') setRole(v);
          }}
          style={inputStyle(C)}
        >
          <option value="admin">Admin</option>
          <option value="reviewer">Reviewer</option>
          <option value="viewer">Viewer</option>
        </select>
      </Field>
      {error !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
      ) : null}
      {success !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg }}>{success}</div>
      ) : null}
      <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="w-full sm:w-auto px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || success !== null}
          className="w-full sm:flex-1 px-4 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 2,
            opacity: submitting || success !== null ? 0.6 : 1,
            cursor: submitting || success !== null ? 'default' : 'pointer',
          }}
        >
          {submitting
            ? 'Sending…'
            : success !== null
              ? 'Sent'
              : 'Send request'}
        </button>
      </div>
    </form>
  );
}

function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    width: '100%',
    background: C.surface2,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: '10px 12px',
    fontFamily: monoFont,
    fontSize: 13,
    outline: 'none',
  };
}

function formatExpiry(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function humanizeInvalid(reason: string): string {
  switch (reason) {
    case 'invalid_name':
      return 'Enter a shorter account name (max 80 characters).';
    case 'invalid_domain':
      return 'That domain doesn’t look right. Try lowercase, no scheme, e.g. acme.com.';
    case 'invalid_email':
      return 'That email doesn’t look right.';
    case 'target_required':
      return 'Enter either the client owner’s email or their domain.';
    case 'domain_not_registered':
      return 'No Sill account uses that domain yet. Create a client account instead, or ask the owner for their email.';
    default:
      return 'The request was rejected.';
  }
}

function humanizeForbidden(reason: string): string {
  switch (reason) {
    case 'agency_feature_disabled':
      return 'The agency feature is not enabled on this account.';
    case 'role_insufficient':
      return 'Only owners and admins may add clients.';
    case 'acting_session_denied':
      return 'Return to your home account first.';
    default:
      return 'Access denied.';
  }
}
